import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, notification } from 'src/common';
import styles from './index.module.scss';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'src/pages/paths';
import { Auth } from 'aws-amplify';
import { useApp, FreeUserAttributes, SignUpProvider, DEFAULT_COUNTRY } from 'src/models/auth';
import CompanyInfo from './CompanyInfo';
import UserInfo from './UserInfo';
import { formatIncompletePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { Config } from 'src/config/config';
import { ReCaptchaProvider } from 'react-recaptcha-x';
import { ownership } from 'src/models/ownership';

export type SignUpFormState = FreeUserAttributes & { password: string; recaptcha: boolean };
export const SignUnPage: FC = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<SignUpFormState>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { setResult } = useApp();
  const initialValues = useMemo(
    (): SignUpFormState => ({
      email: '',
      name: '',
      lastName: '',
      orgName: '',
      orgNumber: '',
      password: '',
      address: '',
      country: DEFAULT_COUNTRY,
      phone: '',
      recaptcha: false
    }),
    []
  );

  const onChange = useCallback(
    (changedValues: Partial<SignUpFormState>) => {
      if (changedValues.phone) {
        const phone = formatIncompletePhoneNumber(changedValues.phone, form.getFieldValue('country'));
        form.setFieldsValue({ phone });
      }
    },
    [form]
  );
  const onFinish = useCallback(
    (values: SignUpFormState) => {
      const {
        email: username,
        password,
        name,
        lastName,
        orgName,
        orgNumber,
        country,
        phone,
        address,
        recaptcha
      } = values;
      setIsLoading(true);
      const intPhone = phone && parsePhoneNumber(phone, country ?? DEFAULT_COUNTRY).number;
      recaptcha &&
        Auth.signUp({
          username,
          password,
          attributes: {
            [`given_name`]: name,
            [`family_name`]: lastName,
            [`custom:orgName`]: orgName,
            [`custom:orgId`]: orgNumber,
            [`custom:country`]: country,
            [`address`]: address,
            [`phone_number`]: intPhone
          },
          clientMetadata: { [`custom:domain`]: ownership.name }
        })
          .then(() => {
            setResult(() => ({
              email: username,
              password,
              loading: false
            }));
            navigate(Paths.CONFIRM_SIGN_UP, { replace: true });
          })
          .catch(error => {
            const { code } = error;
            if (code) {
              // it is a Cognito error
              if (code === 'UsernameExistsException') {
                form.setFields([
                  {
                    name: 'email',
                    errors: [t('Auth.accountAlreadyExist')]
                  }
                ]);
              } else {
                form.setFields([
                  {
                    name: 'email',
                    errors: [error.message]
                  }
                ]);
              }
            } else {
              notification.error({
                message: t('Auth.notSignedUp'),
                description: t('Common.unknownErrorDesc')
              });
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
    },
    [form, navigate, setResult, t]
  );

  return (
    <ReCaptchaProvider siteKeyV2={Config.RecaptchaSiteKey}>
      <Form
        form={form}
        initialValues={initialValues}
        layout={'vertical'}
        className={styles.form}
        onFinish={onFinish}
        onValuesChange={onChange}
      >
        <SignUpProvider>
          <CompanyInfo />
          <UserInfo form={form} loading={isLoading} />
        </SignUpProvider>
      </Form>
    </ReCaptchaProvider>
  );
};

export default SignUnPage;
